import React, { useState } from 'react';
import { Modal, Button, Checkbox, Divider } from 'antd';
import { CloseOutlined, CheckCircleFilled, LeftOutlined } from '@ant-design/icons';
import s from './s.module.less';
import { IAdBudgetDisplay } from 'types/marketing';
import KlarityLogo from 'assets/common/klarity.svg';
import { updateAdBudget } from 'api/marketing';
import { convertBudgetToSave } from 'utils/marketing';
import { PATH } from 'constants/path';
import { useNavigate } from 'react-router-dom';
import { isDevEnv } from 'utils/common';
import useGetOffForProlisting from 'hooks/useGetOffForProlisting';

interface IProps {
    open: boolean;
    onCancel: () => void;
    strategyData?: IAdBudgetDisplay;
    onConfirm?: () => void;
}

const PlatformAgreementUrl = isDevEnv()
    ? 'https://saas-dev-jp.s3.ap-northeast-1.amazonaws.com/dev/ProListingTermsCommonQuestions.html'
    : 'https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/agreement/ProListingTermsCommonQuestions.html';

const ConfirmSaveModal = ({ open, onCancel, strategyData, onConfirm }: IProps) => {
    const [agreed, setAgreed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showAgreement, setShowAgreement] = useState(false);
    const navigate = useNavigate();
    const [off] = useGetOffForProlisting();
    const handleConfirm = async () => {
        if (!agreed || !strategyData) return;
        setLoading(true);
        try {
            const result = await updateAdBudget(
                convertBudgetToSave({
                    ...strategyData,
                    status: undefined,
                }),
            );
            if (!result.error && result.data?.data) {
                if (onConfirm) {
                    onConfirm();
                } else {
                    navigate(PATH.PRO_LISTING_SUCCESS);
                }
            }
            onCancel(); // 关闭弹窗
        } catch (error) {
            console.error('Confirm failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setAgreed(false);
        onCancel();
    };

    const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    return (
        <>
            <Modal
                open={open}
                onCancel={handleCancel}
                footer={null}
                width={700}
                className={s.modal}
                closeIcon={<CloseOutlined className={s.closeIcon} />}
            >
                <div className={s.content}>
                    <div className={s.header}>
                        <img src={KlarityLogo} alt="Klarity Logo" className={s.logo} />
                        <div>
                            <div className={s.title}>Confirm your Pro-Listings campaign</div>
                            <div className={s.subtitle}>Review your setup before launching</div>
                        </div>
                    </div>

                    <div className={s.creditSection}>
                        <CheckCircleFilled className={s.checkIcon} />
                        <div>
                            <div className={s.creditTitle}>${off} free credit automatically applied!</div>
                            <div className={s.creditDesc}>
                                Get ${off} off your first Pro-Listings campaign. You&apos;ll only be charged after the
                                credit is used.
                            </div>
                        </div>
                    </div>

                    <div className={s.strategySection}>
                        <div className={s.strategyTitle}>Applies to all your active service states</div>

                        <div className={s.strategyRow}>
                            <span className={s.strategyLabel}>Bid amount</span>
                            <span className={s.strategyValue}>${strategyData?.generalBidAmount || 40} per booking</span>
                        </div>

                        <div className={s.strategyRow}>
                            <span className={s.strategyLabel}>Budget amount</span>
                            <span className={s.strategyValue}>
                                ${strategyData?.budgetCap || 200}{' '}
                                {(strategyData?.budgetPeriod || 'weekly').toLowerCase()}
                            </span>
                        </div>

                        {strategyData?.enableAdvancedSettings && strategyData?.specialtyBids && (
                            <>
                                <Divider />
                                <div className={`${s.strategyRow} ${s.specialtyBidOverrides}`}>
                                    <span className={s.strategyLabel}>Custom bids by specialty</span>
                                    <span className={s.strategyValue}>
                                        {strategyData.specialtyBids.map((item) => {
                                            const periodLabel = strategyData.budgetPeriod === 'MONTHLY' ? 'mo' : 'wk';
                                            if (item.enabled === false) {
                                                return (
                                                    <div key={item.specialtyName} className={s.specDisabledRow}>
                                                        <span>{item.specialtyName}</span>
                                                        <span>Not running</span>
                                                    </div>
                                                );
                                            }
                                            const capText = item.specialtyCap
                                                ? ` · capped at $${item.specialtyCap}/${periodLabel}`
                                                : '';
                                            return (
                                                <div key={item.specialtyName}>
                                                    {item.specialtyName}: ${item.bidAmount}
                                                    {capText}
                                                </div>
                                            );
                                        })}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>

                    <div className={s.agreementSection}>
                        <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className={s.checkbox}>
                            <span className={s.agreementText}>
                                By checking this box, I agree to the{' '}
                                <a
                                    href="#"
                                    className={s.agreementLink}
                                    onClick={() => {
                                        setShowAgreement(true);
                                    }}
                                >
                                    Klarity Health Pro-Listings Agreement
                                </a>{' '}
                                outlined in the agreement on {today}.
                            </span>
                        </Checkbox>
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        block
                        disabled={!agreed}
                        loading={loading}
                        onClick={handleConfirm}
                        className={s.confirmButton}
                    >
                        Confirm
                    </Button>

                    <div className={s.footerNote}>
                        By confirming, your settings will go live immediately. Charges only apply when a new patient books through your listing. Keeping your balance up to date keeps your listing visible. If it goes unpaid, your listing may be paused until it&apos;s cleared.
                    </div>
                </div>
            </Modal>
            <Modal
                open={showAgreement}
                onCancel={() => setShowAgreement(false)}
                footer={null}
                width={600}
                className={s.modal}
            >
                <div onClick={() => setShowAgreement(false)} className={s.backIcon}>
                    <LeftOutlined />
                </div>
                <iframe className={s.iframe} title="overview" src={PlatformAgreementUrl} />
            </Modal>
        </>
    );
};

export default ConfirmSaveModal;
